import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Monitor, RefreshCw, Search, Clock, Download, Users,
  CheckCircle, XCircle, AlertTriangle, Eye, Settings, Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';

const toLocalFromUtcString = (value) => {
  if (!value) return null;
  try {
    const normalized = typeof value === 'string' ? value.replace(' ', 'T') : value;
    const date = new Date(
      normalized && typeof normalized === 'string' && !normalized.endsWith('Z') ? `${normalized}Z` : normalized
    );
    return date;
  } catch {
    return null;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'online':      return 'bg-green-950/30 text-green-400';
    case 'offline':     return 'bg-muted text-foreground';
    case 'error':       return 'bg-red-950/30 text-red-400';
    case 'maintenance': return 'bg-yellow-950/30 text-yellow-400';
    default:            return 'bg-muted text-foreground';
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'online':  return <CheckCircle className="h-4 w-4" />;
    case 'offline': return <XCircle className="h-4 w-4" />;
    case 'error':   return <AlertTriangle className="h-4 w-4" />;
    default:        return <Clock className="h-4 w-4" />;
  }
};

const TerminalTableCard = ({
  filteredTerminals,
  loading,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  modeFilter,
  setModeFilter,
  autoRefresh,
  setAutoRefresh,
  selectedTerminals,
  setSelectedTerminals,
  handleSelectAllTerminals,
  handleSelectTerminal,
  handleSyncTerminalTime,
  handleSyncTerminalUsers,
  handleFetchMultipleTerminalsAttendance,
  handleSyncMultipleTerminalsTime,
  handleSyncMultipleTerminalsUsers,
  syncingUserData,
  openDetails,
  handleEditTerminal,
  handleDeleteTerminal,
  loadTerminals,
  loadUnconfiguredTerminals,
}) => {
  const { t } = useTranslation();
  return (
  <Card>
    <CardHeader>
      <div className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Monitor className="h-5 w-5" />
          {t("terminalMgmt.table.registered")}
        </CardTitle>
        <div className="flex items-center gap-2">
          <div className="flex items-center space-x-2 text-sm">
            <input
              type="checkbox"
              id="autoRefresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="autoRefresh" className="text-muted-foreground">{t("terminalMgmt.table.autoRefresh")}</label>
          </div>
          <Button variant="outline" size="sm" onClick={() => { loadTerminals(); loadUnconfiguredTerminals(); }}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t("common.refresh")}
          </Button>
        </div>
      </div>
    </CardHeader>

    <CardContent>
      {/* Search and Filters */}
      <div className="flex items-center justify-between space-x-4 mb-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder={t("terminalMgmt.table.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-32"><SelectValue placeholder={t("terminalMgmt.table.statusPlaceholder")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("terminalMgmt.table.all")}</SelectItem>
              <SelectItem value="online">{t("terminalMgmt.table.online")}</SelectItem>
              <SelectItem value="offline">{t("terminalMgmt.table.offline")}</SelectItem>
              <SelectItem value="error">{t("terminalMgmt.table.error")}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={modeFilter} onValueChange={setModeFilter}>
            <SelectTrigger className="w-32"><SelectValue placeholder={t("terminalMgmt.table.modePlaceholder")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("terminalMgmt.table.all")}</SelectItem>
              <SelectItem value="pull">Pull</SelectItem>
              <SelectItem value="push">Push</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedTerminals.length > 0 && (
        <div className="flex items-center justify-between p-3 bg-primary/5 border border-primary/30 rounded-lg mb-4">
          <span className="text-sm font-medium text-foreground">
            {t("terminalMgmt.table.selectedCount", { count: selectedTerminals.length })}
          </span>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={handleSyncMultipleTerminalsTime}
              className="bg-blue-700 hover:bg-blue-800 border-blue-600 text-white">
              <Clock className="h-4 w-4 mr-2" /> {t("terminalMgmt.table.syncTime")}
            </Button>
            <Button variant="outline" size="sm" onClick={handleFetchMultipleTerminalsAttendance}
              className="bg-green-800 hover:bg-green-700 border-green-700 text-white">
              <Download className="h-4 w-4 mr-2" /> {t("terminalMgmt.table.fetchPunches")}
            </Button>
            <Button variant="outline" size="sm" onClick={handleSyncMultipleTerminalsUsers} disabled={syncingUserData}
              className="bg-purple-600 hover:bg-purple-700 border-purple-500 text-white">
              {syncingUserData ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Users className="h-4 w-4 mr-2" />}
              {t("terminalMgmt.table.syncUsers")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setSelectedTerminals([])} className="text-muted-foreground">
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="h-6 w-6 animate-spin mr-2" />
          <span>{t("terminalMgmt.table.loadingTerminals")}</span>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedTerminals.length === filteredTerminals.length && filteredTerminals.length > 0}
                  onCheckedChange={handleSelectAllTerminals}
                />
              </TableHead>
              <TableHead>{t("terminalMgmt.table.colTerminal")}</TableHead>
              <TableHead>{t("terminalMgmt.table.colType")}</TableHead>
              <TableHead>{t("terminalMgmt.table.colMode")}</TableHead>
              <TableHead>{t("terminalMgmt.table.colStatus")}</TableHead>
              <TableHead>{t("terminalMgmt.table.colSite")}</TableHead>
              <TableHead>{t("terminalMgmt.table.colLastContact")}</TableHead>
              <TableHead className="text-right">{t("terminalMgmt.table.colActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTerminals.map((terminal) => (
              <TableRow key={terminal.id}>
                <TableCell>
                  <Checkbox
                    checked={selectedTerminals.includes(terminal.id)}
                    onCheckedChange={() => handleSelectTerminal(terminal.id)}
                  />
                </TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <div className="font-medium">{terminal.name}</div>
                    <div className="text-sm text-muted-foreground">SN: {terminal.serial_number}</div>
                    {terminal.ip_address && (
                      <div className="text-xs text-muted-foreground">{terminal.ip_address}:{terminal.port}</div>
                    )}
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline">{terminal.terminal_type}</Badge></TableCell>
                <TableCell>
                  <Badge variant={terminal.communication_mode === 'push' ? 'default' : 'secondary'}>
                    {terminal.communication_mode === 'push' ? t("terminalMgmt.table.pushAdms") : t("terminalMgmt.table.pullLocal")}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(terminal.status)}>
                    <span className="flex items-center gap-1">
                      {getStatusIcon(terminal.status)}
                      {terminal.status}
                    </span>
                  </Badge>
                </TableCell>
                <TableCell>{terminal.site_name}</TableCell>
                <TableCell>
                  {terminal.last_sync_at || terminal.last_ping_at
                    ? toLocalFromUtcString(terminal.last_sync_at || terminal.last_ping_at)?.toLocaleString()
                    : <span className="text-muted-foreground">{t("terminalMgmt.table.never")}</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end space-x-2">
                    <Button variant="outline" size="sm" onClick={() => handleSyncTerminalTime(terminal.id)}
                      title={t("terminalMgmt.table.syncTimeTitle")} className="bg-primary hover:bg-primary/80 border-primary/40 text-primary-foreground">
                      <Clock className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleSyncTerminalUsers(terminal.id)}
                      disabled={syncingUserData} title={t("terminalMgmt.table.syncUsersTitle")}
                      className="bg-purple-700 hover:bg-purple-600 border-purple-600 text-white">
                      {syncingUserData ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Users className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openDetails(terminal)} title={t("terminalMgmt.table.viewDetails")}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEditTerminal(terminal)}>
                      <Settings className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t("terminalMgmt.table.deleteTitle")}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t("terminalMgmt.table.deleteConfirm", { name: terminal.name })}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteTerminal(terminal.id)}
                            className="bg-red-600 hover:bg-red-700">
                            {t("common.delete")}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {filteredTerminals.length === 0 && !loading && (
        <div className="text-center py-8 text-muted-foreground">{t("terminalMgmt.table.empty")}</div>
      )}
    </CardContent>
  </Card>
  );
};

export default TerminalTableCard;
